import { Controller } from "@hotwired/stimulus"

// Lightbox / image viewer.
//
// Wraps a section of the page in `data-controller="lightbox"` and gathers all
// `<img>` matching `data-lightbox-selector-value` (default: any `img` inside
// `figure`). Click any image to open a full-bleed overlay with prev/next/close
// controls; ESC closes, ← / → navigate.
//
// Honors prefers-reduced-motion by skipping transitions.
export default class extends Controller {
  static values = {
    selector: { type: String, default: "figure img, .post-image img" },
  }

  connect() {
    this.images = Array.from(this.element.querySelectorAll(this.selectorValue))
      .filter((img) => img.getAttribute("src"))
    if (this.images.length === 0) return

    this.reduced = matchMedia("(prefers-reduced-motion: reduce)").matches
    this.boundOnKey = this.onKey.bind(this)
    this.currentIndex = -1

    this.images.forEach((img, i) => {
      img.style.cursor = "zoom-in"
      img.dataset.lightboxIndex = String(i)
      img.addEventListener("click", (e) => {
        e.preventDefault()
        this.open(i)
      })
    })

    this.buildOverlay()
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundOnKey)
    if (this.overlay && this.overlay.parentNode) {
      this.overlay.parentNode.removeChild(this.overlay)
    }
  }

  buildOverlay() {
    const o = document.createElement("div")
    o.className = "lightbox"
    o.setAttribute("aria-hidden", "true")
    o.setAttribute("role", "dialog")
    o.setAttribute("aria-label", "Visor de imagen")
    o.innerHTML = `
      <button class="lightbox-close" type="button" aria-label="Cerrar">×</button>
      <button class="lightbox-prev" type="button" aria-label="Anterior">‹</button>
      <button class="lightbox-next" type="button" aria-label="Siguiente">›</button>
      <figure class="lightbox-stage">
        <img alt="" />
        <figcaption class="lightbox-caption caption-draft"></figcaption>
      </figure>
      <div class="lightbox-counter" aria-live="polite"></div>
    `
    document.body.appendChild(o)
    this.overlay = o
    this.imgEl = o.querySelector("img")
    this.captionEl = o.querySelector(".lightbox-caption")
    this.counterEl = o.querySelector(".lightbox-counter")

    o.querySelector(".lightbox-close").addEventListener("click", () => this.close())
    o.querySelector(".lightbox-prev").addEventListener("click", () => this.prev())
    o.querySelector(".lightbox-next").addEventListener("click", () => this.next())
    o.addEventListener("click", (e) => {
      if (e.target === o) this.close()
    })
  }

  open(i) {
    this.currentIndex = i
    this.update()
    this.overlay.classList.add("is-open")
    this.overlay.setAttribute("aria-hidden", "false")
    document.documentElement.style.overflow = "hidden"
    document.addEventListener("keydown", this.boundOnKey)
  }

  close() {
    this.overlay.classList.remove("is-open")
    this.overlay.setAttribute("aria-hidden", "true")
    document.documentElement.style.overflow = ""
    document.removeEventListener("keydown", this.boundOnKey)
    const restoreTo = this.images[this.currentIndex]
    if (restoreTo) restoreTo.focus({ preventScroll: true })
    this.currentIndex = -1
  }

  prev() {
    if (this.currentIndex < 0) return
    this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length
    this.update()
  }

  next() {
    if (this.currentIndex < 0) return
    this.currentIndex = (this.currentIndex + 1) % this.images.length
    this.update()
  }

  update() {
    const src = this.images[this.currentIndex]
    if (!src) return
    this.imgEl.src = src.currentSrc || src.src
    this.imgEl.alt = src.alt || ""
    this.captionEl.textContent = src.alt || ""
    this.captionEl.style.display = src.alt ? "" : "none"
    this.counterEl.textContent = `${this.currentIndex + 1} / ${this.images.length}`
  }

  onKey(e) {
    if (e.key === "Escape") return this.close()
    if (e.key === "ArrowLeft") return this.prev()
    if (e.key === "ArrowRight") return this.next()
  }
}
